import axios from "axios";
import { endOfWeek, format, startOfWeek, startOfYear } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DateParam, useQueryParam } from "use-query-params";
import CenterLoader from "../components/CenterLoader";
import { UserContext } from "../contexts/UserContext";
import { GroupedCloserData } from "../settlementsDashboard/types";
import { floorPlan } from "./floorPlan";
import { people } from "./people";
import Space from "./Space";
import { Root } from "./styles";
import type { BorderSide, Person } from "./types";
import { getRelationships } from "./utils";

const startOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
const endOfCurrentWeek = endOfWeek(new Date(), { weekStartsOn: 0 });

// const startOfCurrentYear = startOfYear(new Date());

const defaultStartDate = startOfCurrentWeek;
const defaultEndDate = endOfCurrentWeek;

export default function Seating() {
  const [activePerson, setActivePerson] = useState<Person | null>(null);

  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [groupedCloserData, setGroupedCloserData] = useState<GroupedCloserData>({});
  const [relationshipsMap, setRelationshipsMap] = useState<{ [key: string]: string[] }>({});
  const [startDate, setStartDate] = useQueryParam("startDate", DateParam);
  const [endDate, setEndDate] = useQueryParam("endDate", DateParam);

  const isDefaultDates = !startDate && !endDate;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const authorizedPageViewers = [
      "bfreylicher@madisoncres.com",
      "ymeister@madisontitle.com",
      "jshonek@madisontitle.com",
      "mbecker@madisoncres.com",
      "sdavidowitz@madisoncres.com",
      "agoldstein@madisoncres.com",
      "afriedman@madisoncres.com",
      "tnewman@madisoncres.com",
      "hwosner@madisontitle.com",
      "mslanger@madisoncres.com"
    ];

    if (user != null && !authorizedPageViewers.includes(user.email.toLowerCase())) {
      navigate("/unauthorized");
    }
  }, [user]);

  const getSettlementDashboardData = async () => {
    try {
      setIsLoading(true);
      const from = format(startDate || defaultStartDate, "yyyy-MM-dd");
      const to = format(endDate || defaultEndDate, "yyyy-MM-dd");
      const { data } = await axios.get<GroupedCloserData>(
        `/api/settlementsDashboard/GetGroupedCloserFiles?from=${from}&to=${to}`
      );
      setGroupedCloserData(data);
      const names = people.map((p) => p.name);
      setRelationshipsMap(getRelationships(data, names));
    } catch (error) {
      console.error("Failed to fetch settlement data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSettlementDashboardData();
  }, [startDate, endDate]);

  const renderFloor = (floor: "annex" | "title" | "misc", allPeople: Person[]) => {
    const plan = floorPlan.filter((space) => space.floor === floor);
    const people = allPeople.filter((p) => p.floor === floor);
    return (
      <div className={floor === "annex" ? "grid" : floor === "title" ? "gridTwo" : "gridThree"}>
        {plan.map((space) => {
          const foundPerson = people.find((p) => p.seatId === space.id);
          const orderData = groupedCloserData[foundPerson?.name || ""] || {};
          const isActive = activePerson?.seatId === space.id;
          const person = (foundPerson ? { ...foundPerson, funders: orderData.uniqueFunders || [] } : {}) as Person;
          return (
            <Space
              key={space.id}
              type={space.type as "seat" | "printer" | "hallway" | "door"}
              person={person}
              position={space.position}
              border={space.border as BorderSide[]}
              isActive={isActive}
              onHighlightFunders={(person) => setActivePerson(person)}
              activePerson={activePerson}
              orders={orderData.orders || []}
              relationshipsMap={relationshipsMap}
              label={space.label}
              styleOverrides={space.styleOverrides}
              innerStyleOverrides={space.innerStyleOverrides}
            />
          );
        })}
      </div>
    );
  };

  if (!user) return <CenterLoader isOpen />;
  return (
    <Root onClickCapture={() => setActivePerson(null)}>
      <div style={{ paddingBottom: 100 }}>
        <SectionHeader header="Annex" />
        {renderFloor("annex", people)}
        <SectionHeader header="Title" styles={{ marginTop: "80px" }} />
        {renderFloor("title", people)}
        <SectionHeader header="Remote" styles={{ marginTop: "80px", justifyContent: "flex-start" }} isJv />
        {renderFloor("misc", people)}
      </div>
    </Root>
  );
}

function SectionHeader({
  header,
  styles,
  isJv = false
}: {
  header: string;
  styles?: React.CSSProperties;
  isJv?: boolean;
}) {
  return (
    <div className="chartHeader" style={styles}>
      <div className="header">{header}</div>
      <ul className="legend">
        {isJv && <li style={{ backgroundColor: "rgba(186, 104, 200, 0.3)", color: "#000" }}>JV</li>}
        <li style={{ backgroundColor: "rgba(34, 197, 94, 0.16)", color: "#000" }}>National</li>
        <li style={{ backgroundColor: "rgba(0, 184, 217, 0.16)", color: "#000" }}>Residential</li>
        <li style={{ backgroundColor: "rgba(255, 249, 196, 0.6)", color: "#000" }}>Post Closing</li>
        <li style={{ backgroundColor: "rgba(255, 205, 210, 0.6)", color: "#000" }}>NY</li>
        <li style={{ backgroundColor: "rgba(230, 204, 178, 0.6)", color: "#000" }}>Texas</li>
        <li style={{ backgroundColor: "rgba(150, 150, 150, 0.2)", color: "#000" }}>Other</li>
      </ul>
    </div>
  );
}
